<template>

    <AppHead :title="account.name" />

    <section class="px-4 xl:px-0 pb-12">
        <div class="flex flex bg-white rounded-2xl p-6 space-x-6">
            <div class="">
                <div class="shrink-0 mb-3 w-56 h-56 flex items-center justify-center rounded-2xl bg-center bg-cover bg-gray-400 border-2 border-white" :style="'background-image: url(' + account.logo + ')'" >
                    <svg v-if="!account.logo" class="w-8 h-8 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path></svg>
                </div>
                <div class="w-full flex flex-col items-center">
                    <div v-if="!account.logo && !logoUploading" @click="$refs.logo.click()" class="inline-flex border border-gray-400 rounded-full h-12 px-3 pr-4 space-x-1.5 items-center text-gray-800 hover:border-gray-700 hover:text-gray-900 cursor-pointer transition-colors duration-100 ease-out active:border-gray-900">
                        <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"></path><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"></path></svg>
                        <div class="font-medium">
                            Upload logo
                        </div>
                    </div>
                    <div v-if="logoUploading" class="inline-flex border border-gray-400 rounded-full h-12 px-3 pr-4 space-x-1.5 items-center text-gray-700">
                        <svg class="animate-spin h-5 w-5 mr-1 text-gray-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <div class="font-medium">
                            Working
                        </div>
                    </div>
                    <Link v-if="account.logo && !logoUploading" :href="route('accounts.update', account)" :data="{ logo: 'delete'}" as="button" method="patch" class="inline-flex border border-gray-400 rounded-full h-12 px-3 pr-4 space-x-1.5 items-center text-gray-800 hover:border-gray-700 hover:text-gray-900 cursor-pointer transition-colors duration-100 ease-out active:border-gray-900">
                        <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg>
                        <div class="font-medium">
                            Remove logo
                        </div>
                    </Link>
                </div>
            </div>
            <div class="flex-1 flex flex-col">
                <div class="flex justify-between w-full items-center">
                    <div class="text-3xl font-bold tracking-tight line-clamp-1">
                        {{ account.name }}
                    </div>
                    <div @click="accountOpen = true" class="shrink-0 flex border border-gray-400 rounded-full h-12 w-12 justify-center items-center text-gray-800 hover:border-gray-700 hover:text-gray-900 cursor-pointer transition-colors duration-100 ease-out active:border-gray-900">
                        <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"></path></svg>
                    </div>
                </div>
                <div class="flex items-center">
                    <div class="mr-1.5 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-400">
                        {{ account.plan }}
                    </div>
                    <div class="leading-none text-sm font-medium text-gray-700">
                        {{ account.userCount }} · {{ account.episodeCount }}
                    </div>
                </div>
                <div class="flex-1 flex items-end justify-end space-x-2">
                    <Link :href="route('users.index')" class="flex border border-gray-400 rounded-full h-12 w-12 justify-center space-x-1.5 items-center text-gray-800 hover:border-gray-700 hover:text-gray-900 cursor-pointer transition-colors duration-100 ease-out active:border-gray-900">
                        <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path></svg>
                    </Link>
                    <Link :href="route('users.index', {_query: {invite: true}})" class="flex border border-gray-400 rounded-full h-12 px-3 pr-4 space-x-1.5 items-center text-gray-800 hover:border-gray-700 hover:text-gray-900 cursor-pointer transition-colors duration-100 ease-out active:border-gray-900">
                        <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"></path></svg>
                        <div class="font-medium">
                            Invite people
                        </div>
                    </Link>
                </div>
            </div>
        </div>
    </section>

    <input type="file" accept="image/*" ref="logo" @input="uploadLogo" class="hidden"/>

    <section class="px-4 xl:px-0 pb-16">
        <SectionHeading title="Apps & integrations" importance="2" />
        <div class="grid grid-flow-cols grid-cols-3 gap-6">
            <div class="flex items-center bg-white rounded-2xl p-8 space-x-6">
                <div class="">
                    <svg class="w-8" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg" shape-rendering="geometricPrecision"><title>Slack</title><g fill="none"><path d="m21.902.148c-3.299 0-5.973 2.68-5.973 5.985a5.979 5.979 0 0 0 5.973 5.985h5.974v-5.985a5.98 5.98 0 0 0 -5.974-5.985m0 15.96h-15.929c-3.299 0-5.973 2.68-5.973 5.986 0 3.305 2.674 5.985 5.973 5.985h15.93c3.298 0 5.973-2.68 5.973-5.985 0-3.306-2.675-5.986-5.974-5.986" fill="#36c5f0"></path><path d="m59.734 22.094c0-3.306-2.675-5.986-5.974-5.986s-5.973 2.68-5.973 5.986v5.985h5.973a5.98 5.98 0 0 0 5.974-5.985m-15.929 0v-15.961a5.98 5.98 0 0 0 -5.974-5.985c-3.299 0-5.973 2.68-5.973 5.985v15.96c0 3.307 2.674 5.987 5.973 5.987a5.98 5.98 0 0 0 5.974-5.985" fill="#2eb67d"></path><path d="m37.831 60a5.98 5.98 0 0 0 5.974-5.985 5.98 5.98 0 0 0 -5.974-5.985h-5.973v5.985c0 3.305 2.674 5.985 5.973 5.985m0-15.96h15.93c3.298 0 5.973-2.68 5.973-5.986a5.98 5.98 0 0 0 -5.974-5.985h-15.929c-3.299 0-5.973 2.68-5.973 5.985a5.979 5.979 0 0 0 5.973 5.985" fill="#ecb22e"></path><path d="m0 38.054a5.979 5.979 0 0 0 5.973 5.985 5.98 5.98 0 0 0 5.974-5.985v-5.985h-5.974c-3.299 0-5.973 2.68-5.973 5.985m15.929 0v15.96c0 3.306 2.674 5.986 5.973 5.986a5.98 5.98 0 0 0 5.974-5.985v-15.961a5.979 5.979 0 0 0 -5.974-5.985c-3.299 0-5.973 2.68-5.973 5.985" fill="#e01e5a"></path></g></svg>
                </div>
                <div class="">
                    <div class="font-medium leading-tight">
                        Slack
                    </div>
                     <div class="text-sm text-gray-800">
                         Channel notifications
                     </div>
                </div>
                <div class="flex-1 flex justify-end">
                    <Switch v-model="enabled" :class="[enabled ? 'bg-regal-blue-700' : 'bg-gray-400', 'relative inline-flex shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-regal-blue-500']">
                        <span class="sr-only">Use setting</span>
                        <span aria-hidden="true" :class="[enabled ? 'translate-x-5' : 'translate-x-0', 'pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow ring-0 transition ease-in-out duration-200']" />
                    </Switch>
                </div>
            </div>
        </div>
    </section>


    <section class="px-4 xl:px-0 pb-24">
        <SectionHeading title="Single Sign-On (SSO)" importance="2" />
        <div class="flex flex bg-white rounded-2xl p-8 space-x-6">

            <div v-if="connection" class="flex space-x-5 items-center w-full">
                <div class="">
                    <div class="flex rounded-lg items-center justify-center w-8 h-8 mr-1">
                        <div v-html="connection.icon" />
                    </div>
                </div>
                <div class="">
                    <div class="font-medium leading-tight">
                        {{ connection.name }}
                    </div>
                    <div class="text-sm text-gray-800">
                        {{ connection.domain }}
                    </div>
                </div>
                <div class="flex-1 flex justify-end items-center space-x-2">
                    <div @click="ssoOpen = true">
                        <Button label="Edit" type="primary outline"/>
                    </div>
                </div>
            </div>

            <div v-if="!connection" class="bg-white w-full flex items-center justify-center pt-4">
                <div class="w-auto h-32 md:h-64 flex flex-col space-y-5 items-center justify-center">
                    <div class="flex flex-col items-center">
                        <svg class="w-8 h-8 mb-4 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path></svg>
                        <div class="text-sm font-medium mb-2">
                            No connection, yet.
                        </div>
                        <div class="text-gray-700 text-sm text-center max-w-md">
                            SAML 2.0 - based single sign-on gives your team members access to Pager through Google Workspace, Okta, Azure, and more.
                        </div>
                    </div>

                    <div @click="ssoOpen = true">
                        <Button label="Add connection"/>
                    </div>
                </div>
            </div>
        </div>
    </section>


    <Modal v-model:open="ssoOpen">
        <div class="w-full">
            <div v-if="!form.connection">
                <SectionHeading title="Select a SAML SSO provider" />
                <div class="grid grid-cols-3 gap-x-6">
                    <div v-for="ssoConnection in ssoConnections" @click="form.connection = ssoConnection" class="flex flex-col items-center justify-center bg-gray-300 rounded-xl p-4 cursor-pointer hover:bg-gray-400 transition-colors duration-100 ease-out">
                        <div class="w-full flex items-center justify-center py-9">
                            <div class="flex rounded-lg items-center justify-center w-10 h-10 mr-1">
                                <div v-html="ssoConnection.icon" />
                            </div>
                        </div>
                        <div class="w-full mb-2.5">
                            <div class="font-medium text-gray-800 text-center w-full ">
                                {{ ssoConnection.name }}
                            </div>
                            <div class="text-sm text-gray-700 w-full text-center">
                                {{ ssoConnection.description }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="form.connection">
                <SectionHeading :title="'Set up ' + form.connection.name" />
                <div class="text-regal-blue-800 mb-8 flex space-x-2.5 items-center w-full bg-regal-blue-100 text-sm rounded-lg p-4">
                    <div class="">
                        <svg class="w-5 h-5 text-regal-blue-600" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"></path></svg>
                    </div>
                    <div class="">
                        Get help to set up <a class="underline hover:no-underline" :href="form.connection.helpUrl">{{ form.connection.name }}</a>. If you need further assitance, <a class="underline hover:no-underline" href="mailto:hello@pager.fm">contact us</a>.
                    </div>
                </div>
                <div class="w-full">
                    <form @submit.prevent="createSsoConnection">
                        <div v-if="form.connection.slug == 'google'" class="space-y-8">
                            <div class="grid grid-cols-4 gap-x-5">
                                <div class="">
                                    <label for="domain" class="block font-medium text-gray-800 sm:mt-px sm:pt-2">
                                        Google workspace domain
                                    </label>
                                </div>
                                <div class="col-span-3">
                                    <input type="text" placeholder="yourcompany.com" name="domain" v-model="googleSsoForm.domain" class="rounded-lg w-full" required>
                                    <InlineError v-if="googleSsoForm.errors.domain" :message="googleSsoForm.errors.domain"/>
                                </div>
                            </div>
                            <div class="grid grid-cols-4 gap-x-6">
                                <div class="">
                                    <label for="client_id" class="block font-medium text-gray-800 sm:mt-px sm:pt-2">
                                        Client ID
                                    </label>
                                </div>
                                <div class="col-span-3">
                                    <input type="text" placeholder="390004927959-38rina98kj32ja68201794l1vrhjqhcr.apps.googleusercontent.com" name="client_id" v-model="googleSsoForm.clientId" class="rounded-lg w-full" required>
                                    <InlineError v-if="googleSsoForm.errors.clientId" :message="googleSsoForm.errors.clientId"/>
                                </div>
                            </div>
                            <div class="grid grid-cols-4 gap-x-6">
                                <div class="">
                                    <label for="client_secret" class="block font-medium text-gray-800 sm:mt-px sm:pt-2">
                                        Client secret
                                    </label>
                                </div>
                                <div class="col-span-3">
                                    <input v-if="!googleSsoForm.clientSecret" type="text" name="client_secret" placeholder="G0CSPX-7H01HrrIGZhlZ4qPMviFptGfokOl" v-model="googleSsoForm.clientSecret" class="rounded-lg w-full" required>
                                    <input v-if="googleSsoForm.clientSecret" type="password" name="client_secret" placeholder="G0CSPX-7H01HrrIGZhlZ4qPMviFptGfokOl" v-model="googleSsoForm.clientSecret" class="rounded-lg w-full" required>
                                    <InlineError v-if="googleSsoForm.errors.clientSecret" :message="googleSsoForm.errors.clientSecret"/>
                                </div>
                            </div>
                            <div class="w-full flex justify-center border-t border-t-gray-400 pt-8 justify-between">
                                <div @click="deleteSsoConnection">
                                    <Button label="Delete connection" :disabled="form.processing" type="danger"/>
                                </div>
                                <button type="submit">
                                    <Button label="Save" :disabled="form.processing"/>
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>

        </div>

    </Modal>


    <Modal v-model:open="accountOpen">
        <div class="w-full">
            <form @submit.prevent="updateAccount">
                <div class="space-y-8">
                    <div class="grid grid-cols-4 gap-x-6">
                        <div class="">
                            <label for="name" class="block font-medium text-gray-800 sm:mt-px sm:pt-2">
                                Account name
                            </label>
                        </div>
                        <div class="col-span-3">
                            <input type="text" name="name" v-model="form.name" class="rounded-lg w-full" required>
                            <InlineError v-if="form.errors.name" :message="form.errors.name"/>
                        </div>
                    </div>
                    <div class="grid grid-cols-3 divide-x divide-gray-400 border border-gray-400 rounded-xl">
                        <div class="p-4">
                            <div class="font-serif text-2xl mb-2">
                                Standard
                            </div>
                            <div class="mb-8 text-sm text-gray-800">
                                Collaborative power for fast-growing teams.
                            </div>
                            <div class="text-4xl font-medium flex items-start space-x-0.5 mb-2">
                                <div class="text-xl font-medium pt-0.5">$</div>
                                <div class="tracking-tight">
                                    120
                                </div>
                            </div>
                            <div class="flex">
                                <select class="rounded-lg w-full text-sm text-regal-blue-700 border-transparent p-0 py-1 focus:outline-none focus:ring-transparent focus:border-transparent outline-none appearance-none" name="">
                                    <option value="" v-for="i in 99" :selected="i == 3" class="focus:outline-none outline-none focus:border-transparent focus:ring-transparent">{{ i }} members</option>
                                </select>
                            </div>
                            <div class="text-sm text-gray-700 -mt-1.5 mb-4">
                                /month billed annually
                            </div>
                            <div class="">
                                <Button label="Upgrade" class="w-full" type="primary outline"/>
                            </div>

                        </div>
                        <div class="p-4">
                            <div class="font-serif text-2xl mb-2">
                                Plus
                            </div>
                            <div class="mb-8 text-sm text-gray-800">
                                Advanced security and scale for larger companies.
                            </div>
                            <div class="text-4xl font-medium flex items-start space-x-0.5 mb-2">
                                <div class="text-xl font-medium pt-0.5">$</div>
                                <div class="tracking-tight">
                                    240
                                </div>
                            </div>
                            <div class="flex">
                                <select class="rounded-lg w-full text-sm text-regal-blue-700 border-transparent p-0 py-1 focus:outline-none focus:ring-transparent focus:border-transparent outline-none appearance-none" name="">
                                    <option value="" v-for="i in 500" :selected="i == 120" class="focus:outline-none outline-none focus:border-transparent focus:ring-transparent">{{ i }} members</option>
                                </select>
                            </div>
                            <div class="text-sm text-gray-700 -mt-1.5 mb-4">
                                /month billed annually
                            </div>
                            <div class="">
                                <Button label="Upgrade" class="w-full" type="primary outline"/>
                            </div>
                        </div>
                        <div class="p-4 grid grid-flow-row grid-rows-12">
                            <div class="col-span-3">
                                <div class="font-serif text-2xl mb-2">
                                    Enterprise
                                </div>
                                <div class="mb-8 text-sm text-gray-800">
                                    Additional security, control and support.
                                </div>
                            </div>
                            <div class="col-span-4">
                                <div class="text-4xl font-medium flex items-start space-x-0.5 mb-2">
                                    <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"></path></svg>
                                </div>
                                <div class="flex text-sm">
                                    Custom pricing
                                </div>
                                <div class="text-sm text-gray-700 mb-4">
                                    Contact us for more info
                                </div>
                            </div>
                            <div class="col-span-3">
                            </div>
                            <div class="col-span-2 w-full">
                                <div class="w-full">
                                    <Button label="Contact us" class="w-full" type="primary outline"/>
                                </div>
                            </div>

                        </div>
                    </div>
                    <div class="w-full flex justify-center justify-between">
                        <div @click="deleteAccount">
                            <Button label="Delete account" :disabled="form.processing" type="danger"/>
                        </div>
                        <button type="submit">
                            <Button label="Save" :disabled="form.processing"/>
                        </button>
                    </div>
                </div>
            </form>
        </div>
    </Modal>
</template>

<script>
    import Layout from '@/Shared/Layout'
    import { useForm } from '@inertiajs/inertia-vue3'
    import SectionHeading from '@/Shared/SectionHeading'
    import Modal from '@/Shared/Modal'
    import Button from '@/Shared/Button'
    import { ref } from 'vue'
    import { Switch } from '@headlessui/vue'
    import InlineError from '@/Shared/InlineError'

    export default {
        setup (props) {
            const accountOpen = ref(false)
            const ssoOpen = ref(false)
            const logoUploading = ref(false)
            const enabled = ref(false)

            const form = useForm({
                _method: 'patch',
                name: props.account.name ?? 'Untitled Workspace',
                logo: props.account.logo ?? null,
                connection: props.connection ?? null,
            })


            const googleSsoForm = useForm({
                _method: 'patch',
                domain: props.connection ? props.connection.domain : null,
                clientId:  props.connection ? props.connection.clientId : null,
                clientSecret:  props.connection ? props.connection.clientSecret : null,
                action: null,
            })


            return { form, logoUploading, ssoOpen, accountOpen, enabled, googleSsoForm }
        },
        components: { SectionHeading, Button, Modal, Switch, InlineError },
        props: {
            account: Object,
            connection: [Object, Boolean],
            users: Object,
            ssoConnections: Object,
        },
        layout: Layout,
        methods: {
            updateAccount() {
                this.form.patch(route('accounts.update', this.account), {
                    onSuccess: () => {
                        this.accountOpen = false
                        this.logoUploading = false
                        this.form.reset('logo')
                    }
                })
            },
            uploadLogo() {
                this.logoUploading = true
                Vapor.store(this.$refs.logo.files[0], {
                    progress: progress => {
                        this.uploadProgress = Math.round(progress * 100)
                    }
                }).then(response => {
                    this.form.logo = {
                        uuid: response.uuid,
                        key: response.key,
                        bucket: response.bucket,
                        name: this.$refs.logo.files[0].name,
                        content_type: this.$refs.logo.files[0].type,
                        extension: this.$refs.logo.files[0].name.split('.').pop() ?? null,
                    }
                })
                .then(r => {
                    this.updateAccount()
                })
            },
            createSsoConnection () {
                if (this.connection) {
                    return this.updateSsoConnection()
                }
                this.googleSsoForm.patch(route('accounts.update', this.account), {
                    onSuccess: () => {
                        this.ssoOpen = false
                    }
                })
            },
            deleteSsoConnection() {
                this.googleSsoForm.transform((data) => ({ ...data, action: 'delete' })).patch(route('accounts.update', this.account), {
                    onSuccess: () => {
                        this.ssoOpen = false
                        this.form.connection = null
                        this.googleSsoForm.domain = null
                        this.googleSsoForm.clientId = null
                        this.googleSsoForm.clientSecret = null
                    }
                })
            },
            updateSsoConnection() {
                this.googleSsoForm.transform((data) => ({ ...data, action: 'update' })).patch(route('accounts.update', this.account), {
                    onSuccess: () => {
                        this.ssoOpen = false
                    }
                })
            }
        },
    }
</script>
